import { resolveNextAnniversary, yearOffset } from '@fruitful/fruitils';
import {
    formatOffsetYearRange,
    formatSessionAllowanceLabel,
} from '@fruitful/guidance';
import type { GuidanceSession } from '@fruitful/types';
import { isValid, parseISO } from 'date-fns';

const MAX_MEMBERSHIP_YEARS = 20;

export interface GroupedSessionRow {
    id: string;
    yearKey: string;
    yearLabel: string;
    /** Display order. Newer membership years come first. */
    sortIndex: number;
    session: GuidanceSession | null;
}

export const membershipYearLabel = ({
    yearEnd,
    yearOffset,
    productDisplayName,
    allowanceLabel,
}: {
    yearEnd: Date;
    yearOffset: number;
    productDisplayName: string;
    allowanceLabel: string;
}): string => {
    const range = formatOffsetYearRange(yearEnd, yearOffset);
    if (yearOffset !== 0) {
        return range;
    }

    return `Current year: ${range} - ${productDisplayName} membership, annual allowance: ${allowanceLabel}`;
};

export const groupSessionRowsByMembershipYear = ({
    membershipYearEndsAt,
    now,
    sessions,
    productDisplayName,
    sessionTypeAnnualLimit,
}: {
    membershipYearEndsAt: Date;
    now: Date;
    sessions: GuidanceSession[];
    productDisplayName: string;
    sessionTypeAnnualLimit: number | null | undefined;
}): GroupedSessionRow[] => {
    if (!isValid(membershipYearEndsAt)) {
        return [];
    }

    const allowanceLabel = formatSessionAllowanceLabel(sessionTypeAnnualLimit);

    const yearEnd = resolveNextAnniversary(
        membershipYearEndsAt,
        now,
        MAX_MEMBERSHIP_YEARS,
    );
    const sessionsByYear = new Map<number, GuidanceSession[]>();

    for (const session of sessions) {
        const start = parseISO(session.startTime);
        const offset = isValid(start)
            ? yearOffset(yearEnd, start, MAX_MEMBERSHIP_YEARS)
            : 0;
        const existing = sessionsByYear.get(offset);
        if (existing != null) {
            existing.push(session);
        } else {
            sessionsByYear.set(offset, [session]);
        }
    }

    const offsets = [...sessionsByYear.keys()];
    const minOffset = offsets.length === 0 ? 0 : Math.min(0, ...offsets);
    const maxOffset = offsets.length === 0 ? 0 : Math.max(0, ...offsets);

    const rows: GroupedSessionRow[] = [];
    let sortIndex = 0;
    for (let offset = maxOffset; offset >= minOffset; offset -= 1) {
        const yearLabel = membershipYearLabel({
            yearEnd,
            yearOffset: offset,
            productDisplayName,
            allowanceLabel,
        });
        const yearKey = String(offset);
        const yearSessions = (sessionsByYear.get(offset) ?? [])
            .slice()
            .sort(
                (a, b) =>
                    parseISO(b.startTime).getTime() -
                    parseISO(a.startTime).getTime(),
            );

        if (yearSessions.length === 0) {
            rows.push({
                id: `none-${offset}`,
                yearKey,
                yearLabel,
                sortIndex,
                session: null,
            });
            sortIndex += 1;
            continue;
        }

        for (const session of yearSessions) {
            rows.push({
                id: session.id,
                yearKey,
                yearLabel,
                sortIndex,
                session,
            });
            sortIndex += 1;
        }
    }

    return rows;
};
